import { jwtDecode } from "jwt-decode";
import { AppPlatform, AppSession, DecodedToken, ExternalUser } from "./account";
import apiClient, {
  BaseOperationResult,
  OperationResult,
} from "../../services/apiClient";
import {
  clearUserAuthLogging,
  logSignIn,
  setUserAuthLogging,
} from "../../services/logging";
import { ResetPasswordData } from "./account.data";
import { refreshSessionId } from "../analytics/analyticsBatcher";
import { setImageVersion } from "../images/image";

export const logout = () => {
  setAuthToken();
  window.location.href = "/login";
};

// Some Android in-app WebViews run with DOM storage off, so `localStorage` is null (CM2-2MB). Reading the
// token there crashed the error logger itself, which hid the original error. Fall back to memory so the
// session still works until the page reloads.
let memoryToken: string | null = null;

const tokenStore = (): Storage | null => {
  try {
    return window.localStorage ?? null;
  } catch {
    return null; // SecurityError when storage is blocked
  }
};

export const getAuthToken = () => {
  const store = tokenStore();
  return store ? store.getItem("access_token") : memoryToken;
};

export const setAuthToken = (token?: string) => {
  const store = tokenStore();
  if (token) {
    if (store) store.setItem("access_token", token);
    else memoryToken = token;
    // Refresh analytics session ID to use the token's server-generated session ID
    refreshSessionId();
  } else {
    if (store) store.removeItem("access_token");
    memoryToken = null;
  }
};

export const getDecodedToken = (token?: string): DecodedToken => {
  const t = token ?? getAuthToken();
  return t ? jwtDecode(t) : null;
};

export const authenticate = async (request, admin) => {
  const op = admin ? "auth/admin-login" : "auth/authenticate";
  const res = await apiClient.post<AppSession>(op, request);
  if (res.data.succeeded) {
    setAuthToken(res.data.token);
    logSignIn(res.data.provider);
    setUserAuthLogging(res.data);
  }
  return res.data;
};

export const signup = async (request) => {
  const res = await apiClient.post<AppSession>("auth/signup", request);
  if (res.data.succeeded) {
    setAuthToken(res.data.token);
    logSignIn(res.data.provider);
    // I47 - boots Intercom with the identity, as authenticate() does. Signup navigates in-app to
    // /start, so without this the whole first session stayed an anonymous visitor in Intercom.
    setUserAuthLogging(res.data);
  }
  return res.data;
};

export const isLoggedIn = () => {
  const token = getAuthToken();
  if (token) {
    const currentTime = new Date().getTime() / 1000;
    if (currentTime < getDecodedToken()?.exp) {
      return true;
    }
  }
  return false;
};

export const isSuperuser = () => {
  const token = getAuthToken();
  if (token) {
    if (getDecodedToken()?.su === "True") {
      return true;
    }
  }
  return false;
};

// S71 — the `sup` claim (roles `crm` or `siteadmin`). Gates /support only, and is strictly
// narrower than `su`. A siteadmin holds both, so it can never be locked out of the console.
export const isSupport = () => {
  const token = getAuthToken();
  if (token) {
    if (getDecodedToken()?.sup === "True") {
      return true;
    }
  }
  return false;
};

// S71 — the `lgn` claim (roles `login` or `siteadmin`). Gates /admin-login, and mirrors exactly
// what the server enforces in AdminLoginAsync via User.CanImpersonate. Independent of `su`:
// a `login`-only holder has this and no superuser rights.
export const canImpersonate = () => {
  const token = getAuthToken();
  if (token) {
    if (getDecodedToken()?.lgn === "True") {
      return true;
    }
  }
  return false;
};

export const getImpersonatedBy = () => {
  const token = getAuthToken();
  if (token) {
    const impersonatedBy = getDecodedToken()?.im;
    if (impersonatedBy) {
      return impersonatedBy;
    }
  }
  return null;
};

export const loadAuthSession = async () => {
  if (isLoggedIn()) {
    try {
      // Auth session extend can be slow during high load
      // Use 30s timeout to prevent hanging during background refresh
      const res = await apiClient.get<AppSession>("auth/extend-session", {
        timeout: 30000, // 30 seconds for auth session refresh
      });
      if (res.data?.succeeded) {
        setAuthToken(res.data.token);
        setUserAuthLogging(res.data);
        // S72 - image URLs carry this as `?v=`. Set on every session load so a replace made in
        // another tab or by an import is picked up on the next refresh.
        setImageVersion(res.data.account?.config?.imageVersion);
        return res.data;
      }
      logout();
    } catch {
      logout();
      clearUserAuthLogging();
      return null;
    }
  }
  return null;
};

export const loadExtUser = async (token: string) => {
  const res = await apiClient.get<ExternalUser>(
    "auth/external-user?token=" + token
  );
  return res.data as ExternalUser;
};

export const switchAccount = async (id: number) => {
  const res = await apiClient.post<AppSession>("auth/switch?id=" + id);
  if (res.data.succeeded) {
    setAuthToken(res.data.token);
    return true;
  }
  return false;
};

export const forgotPassword = async (
  email: string
): Promise<OperationResult<boolean>> => {
  const response = await apiClient.post<BaseOperationResult>(
    "/auth/forgot-password",
    { email }
  );
  return response.data;
};

export const resetPassword = async (
  data: ResetPasswordData
): Promise<OperationResult<boolean>> => {
  const response = await apiClient.post<BaseOperationResult>(
    "/auth/reset-password",
    data
  );
  return response.data;
};

export const joinAccount = async (
  email: string,
  code: string
): Promise<AppSession> => {
  const res = await apiClient.post<AppSession>("/auth/join", { email, code });
  if (res.data.succeeded && !res.data.requiresSignup) {
    setAuthToken(res.data.token);
    logSignIn(res.data.provider);
  }
  return res.data;
};

// Pending workspace invite (code + email), persisted by the /join page so the
// join can complete after the user authenticates by ANY method — email+password
// or SSO, from /login or /signup. sessionStorage survives the full-page OAuth
// redirect (router state does not), so this is the robust carrier for the invite.
const PENDING_JOIN_KEY = "cm_pending_join";

export const setPendingJoin = (code: string, email: string) => {
  try {
    sessionStorage.setItem(PENDING_JOIN_KEY, JSON.stringify({ code, email }));
  } catch {
    /* storage unavailable — caller falls back to URL/state threading */
  }
};

/** Reads and removes the pending join (consume-once). */
export const consumePendingJoin = (): { code: string; email: string } | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_JOIN_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_JOIN_KEY);
    const parsed = JSON.parse(raw);
    if (parsed?.code && parsed?.email) {
      return { code: parsed.code, email: parsed.email };
    }
  } catch {
    /* malformed / unavailable */
  }
  return null;
};

export const clearPendingJoin = () => {
  try {
    sessionStorage.removeItem(PENDING_JOIN_KEY);
  } catch {
    /* ignore */
  }
};

export const createAccount = async (name: string) => {
  const res = await apiClient.post<AppSession>("account/create-account", {
    name,
  });
  if (res.data.succeeded) {
    setAuthToken(res.data.token);
    return null;
  }
  return res.data.message;
};

export const setLoginPreference = (platform?: AppPlatform) => {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1); // 1 year
  document.cookie = `cm_last_login=${
    platform || "cm2"
  }; expires=${expires.toUTCString()}; domain=.catalogmachine.com; path=/`;
};

export const setPlatformGroup = (platform: AppPlatform) => {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `cm_ab_group=${platform}; expires=${expires.toUTCString()}; domain=.catalogmachine.com; path=/`;
};
